import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Input, Button, message, Modal, Form, Card } from 'antd';
import { SearchOutlined, QrcodeOutlined } from '@ant-design/icons';
import NavbarMex from '../components/navbarMex';
import './devolucion.css';
import { Html5Qrcode } from 'html5-qrcode';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const Devolucion = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [enlistmentData, setEnlistmentData] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedNumber, setScannedNumber] = useState('');
  const [expandedObservations, setExpandedObservations] = useState({});
  const [manualPreenvio, setManualPreenvio] = useState('');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [preenvioToConfirm, setPreenvioToConfirm] = useState('');
  const usuario_id = localStorage.getItem('usuario_id');

  const isMobile = window.innerWidth <= 768;

  const fetchEnlistmentData = async () => {
    try {
      const response = await axios.get(`https://integration.99envios.app/api/listar-devoluciones-sin-confirmar/${usuario_id}`);
      const formattedData = response.data.map((item, index) => {
        return {
          key: index,
          id: index + 1,
          numero_preenvio: item.numero_preenvio,
          valor_declarado: item.valor_declarado,
          valor_servicio: item.valor_servicio,
          observaciones: item.observaciones,
          dice_contener: item.dice_contener,
          aplica_contrapago: item.aplica_contrapago
        };
      });
      setEnlistmentData(formattedData);
    } catch (error) {
      console.error('Error fetching enlistment data:', error);
      message.error('Error al cargar los datos de devoluciones');
    }
  };

  useEffect(() => {
    fetchEnlistmentData();
  }, [usuario_id]);

  useEffect(() => {
    const filtered = enlistmentData.filter(item =>
      Object.values(item).some(value =>
        value && value.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );
    setFilteredData(filtered);
  }, [searchText, enlistmentData]);

  const handleGlobalScan = () => {
    setIsScanning(true);
  };

  const startScanning = () => {
    const html5QrCode = new Html5Qrcode("qr-reader");
    const config = { fps: 10, qrbox: { width: 250, height: 250 } };

    html5QrCode.start(
      { facingMode: "environment" }, 
      config,
      (decodedText) => {
        html5QrCode.stop().then(() => {
          setScannedNumber(decodedText);
          setIsScanning(false);
          sendScannedNumber(decodedText);
        }).catch((err) => {
          console.error('Error deteniendo el escaneo:', err);
          message.error('Error al detener el escaneo');
        });
      },
      (errorMessage) => {
        console.warn('Error en el escaneo:', errorMessage);
      }
    ).catch((err) => {
      console.error('Error inicializando el escaneo:', err);
      message.error('Error al iniciar el escaneo');
    });
  };

  const sendScannedNumber = async (number) => {
    try {
      const scannedValue = String(number).trim();
      console.log('Valor escaneado:', scannedValue);
      
      const item = enlistmentData.find((item) => String(item.numero_preenvio).trim() === scannedValue);
      
      if (!item) {
        message.error('Número de preenvío no encontrado en la lista');
        return;
      }

      setPreenvioToConfirm(scannedValue);
      setConfirmModalVisible(true);
    } catch (error) {
      message.error('Error al procesar la devolución');
      console.error('Error al procesar la devolución:', error);
    }
  };

  const handleConfirmDevolucion = async () => {
    try {
      await axios.post(`https://integration.99envios.app/api/confirmar-devolucion/${usuario_id}`, {
        numero_preenvio: preenvioToConfirm
      });
      
      message.success(`Devolución ${preenvioToConfirm} confirmada exitosamente`);
      setConfirmModalVisible(false);
      setPreenvioToConfirm('');
      fetchEnlistmentData();
    } catch (error) {
      message.error('Error al confirmar la devolución');
      console.error('Error al confirmar la devolución:', error);
    }
  };

  const handleCancelDevolucion = () => {
    setConfirmModalVisible(false);
    setPreenvioToConfirm('');
  };

  const toggleExpandObservation = (id) => {
    setExpandedObservations(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleManualInput = async () => {
    if (!manualPreenvio.trim()) {
      message.warning('Por favor ingrese un número de preenvío');
      return;
    }
    
    sendScannedNumber(manualPreenvio);
    setManualPreenvio('');
  };

  const downloadManifest = async () => {
    try {
      message.loading('Generando manifiesto...', 0);
      
      // Verify user ID is available
      if (!usuario_id) {
        message.destroy();
        message.error('Error: No se pudo identificar el usuario');
        return;
      }
      
      const response = await axios.get(`https://integration.99envios.app/api/listar-devoluciones-dia/${usuario_id}`)
        .catch(error => {
          console.error('Error API:', error);
          throw new Error(`Error al obtener datos: ${error.message}`);
        });
      
      // Verify response data structure
      if (!response || !response.data) {
        message.destroy();
        message.error('Error: Respuesta inválida del servidor');
        return;
      }
      
      if (response.data.length === 0) {
        message.destroy();
        message.warning('No hay devoluciones registradas para el día de hoy');
        return;
      }

      try {
        // Create PDF with error handling
        const doc = new jsPDF();
        
        // Add title
        const title = 'Manifiesto de devolución del día';
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(title, doc.internal.pageSize.width / 2, 20, { align: 'center' });
        
        // Add current date
        const today = new Date();
        const formattedDate = today.toLocaleDateString('es-MX');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Fecha: ${formattedDate}`, doc.internal.pageSize.width / 2, 30, { align: 'center' });
        
        // Add table with return data
        const tableColumn = ["#", "No. Preenvío", "Dice Contener", "Valor Declarado"];
        const tableRows = [];

        // Add data rows with validation
        response.data.forEach((item, index) => {
          // Make sure all values exist or set defaults
          const preenvio = item.numero_preenvio || 'N/A';
          const contenido = item.dice_contener || 'N/A';
          let valorDeclarado = 'N/A';
          
          try {
            if (item.valor_declarado) {
              const valor = parseFloat(item.valor_declarado);
              if (!isNaN(valor)) {
                valorDeclarado = `$${valor.toFixed(2)}`;
              }
            }
          } catch (e) {
            console.warn(`Error formatting value for row ${index + 1}:`, e);
          }
          
          const dataRow = [index + 1, preenvio, contenido, valorDeclarado];
          tableRows.push(dataRow);
        });

        // Use autoTable correctly with the doc object
        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: 40,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
          margin: { top: 40 }
        });
        
        // Calculate Y position after table for signatures
        const finalY = (doc.lastAutoTable?.finalY || 40) + 30;
        
        // Add signature lines
        doc.line(20, finalY, 90, finalY);
        doc.line(doc.internal.pageSize.width - 20, finalY, doc.internal.pageSize.width - 90, finalY);
        
        // Add signature texts
        doc.text('Entregado por', 55, finalY + 10, { align: 'center' });
        doc.text('Recibido por', doc.internal.pageSize.width - 55, finalY + 10, { align: 'center' });
        
        // Save the PDF with try-catch
        try {
          doc.save(`Manifiesto_Devoluciones_${formattedDate.replace(/\//g, '-')}.pdf`);
          message.destroy();
          message.success('Manifiesto generado correctamente');
        } catch (saveError) {
          console.error('Error saving PDF:', saveError);
          throw new Error('Error al guardar el PDF');
        }
      } catch (pdfError) {
        console.error('Error generating PDF:', pdfError);
        throw new Error(`Error al generar el PDF: ${pdfError.message}`);
      }
    } catch (error) {
      message.destroy();
      console.error('Error en generación de manifiesto:', error);
      message.error(`Error al generar el manifiesto: ${error.message || 'Error desconocido'}`);
    }
  };

  const columnsEnlistment = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Número Preenvío',
      dataIndex: 'numero_preenvio',
      key: 'numero_preenvio',
    },
    {
      title: 'Valor Declarado',
      dataIndex: 'valor_declarado',
      key: 'valor_declarado',
    },
    {
      title: 'Valor Servicio',
      dataIndex: 'valor_servicio',
      key: 'valor_servicio',
    },
    {
      title: 'Dice Contener',
      dataIndex: 'dice_contener',
      key: 'dice_contener',
    },
    {
      title: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      render: (value, record) => {
        if (!value) return 'Sin observaciones';
        
        if (value.length > 50) {
          const isExpanded = expandedObservations[record.id];
          
          return (
            <div>
              {isExpanded ? value : `${value.substring(0, 50)}...`}
              <Button 
                type="link" 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpandObservation(record.id);
                }}
                style={{ padding: '0 4px' }}
              >
                {isExpanded ? 'Ver menos' : 'Ver más'}
              </Button>
            </div>
          );
        }
        
        return value;
      },
    },
    {
      title: 'Aplica Contrapago',
      dataIndex: 'aplica_contrapago',
      key: 'aplica_contrapago',
      render: (value) => (value === 1 ? 'Sí' : 'No'),
    }
  ];

  return (
    <>
      <NavbarMex title="Devoluciones" />
      <div className="misPedidos-me">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '16px', 
          flexWrap: 'wrap', 
          gap: '10px',
        }}>
          <Input
            placeholder="Buscar"
            prefix={<SearchOutlined />}
            style={{ width: 200, height: '42px' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="middle"
          />
          <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '16px' 
          }}>
            <Button 
              type="primary" 
              style={{ 
                height: '42px',
                backgroundColor: '#1890ff',
                fontWeight: 'bold'
              }}
              size="large"
              onClick={downloadManifest}
            >
              Descargar manifiesto de devolución del día
            </Button>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            flexWrap: 'wrap',
            flex: 1,
            justifyContent: 'center',
            maxWidth: isMobile ? '100%' : '650px',
          }}>
            <Input
              placeholder="Digitar número de preenvío"
              value={manualPreenvio}
              onChange={(e) => setManualPreenvio(e.target.value)}
              style={{ flex: 1, minWidth: '150px', height: '42px' }}
              onPressEnter={handleManualInput}
              size="middle"
            />
            <Button 
              onClick={handleManualInput}
              style={{ height: '42px' }}
              size="middle"
            >
              Confirmar
            </Button>
            <Button 
              type="primary" 
              icon={<QrcodeOutlined />}
              onClick={handleGlobalScan}
              style={{ height: '42px' }}
              size="middle"
            >
              Escanear
            </Button>
          </div>
        </div>
        
        <div className="misPedidos-tabla" style={{ overflowX: 'auto', marginTop: '16px' }}>
          <h2>Devoluciones</h2>
          {isMobile ? (
            <>
              {filteredData.map((item, index) => (
                <div key={index} className="card-pedidos-me">
                  <div className="card-header-pedidos-me" style={{ textAlign: 'center' }}>
                    <strong>Número Preenvío:</strong> <span>{item.numero_preenvio}</span>
                  </div>
                  <div className="subcard-pedidos-me">
                    <div className="card-item-pedidos-me" style={{ textAlign: 'left' }}><strong>Dice Contener:</strong> <span>{item.dice_contener}</span></div>
                    <div className="card-item-pedidos-me" style={{ textAlign: 'left' }}><strong>Valor Declarado:</strong> <span>${item.valor_declarado}</span></div>
                    <div className="card-item-pedidos-me" style={{ textAlign: 'left' }}><strong>Valor Servicio:</strong> <span>${item.valor_servicio}</span></div>
                    <div className="card-item-pedidos-me" style={{ textAlign: 'left' }}><strong>Aplica Contrapago:</strong> <span>{item.aplica_contrapago === 1 ? 'Sí' : 'No'}</span></div>
                    {item.observaciones && (
                      <div className="card-item-pedidos-me" style={{ textAlign: 'left' }}>
                        <strong>Observaciones:</strong> 
                        <div style={{ textAlign: 'left' }}>
                          {item.observaciones.length > 50 && !expandedObservations[item.id]
                            ? `${item.observaciones.substring(0, 50)}... ` 
                            : item.observaciones}
                          {item.observaciones.length > 50 && (
                            <Button 
                              type="link" 
                              onClick={() => toggleExpandObservation(item.id)}
                              style={{ padding: '0 4px' }}
                            >
                              {expandedObservations[item.id] ? 'Ver menos' : 'Ver más'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Table
              columns={columnsEnlistment}
              dataSource={filteredData}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: false,
              }}
              scroll={{ x: 1200 }}
              locale={{ emptyText: 'No hay datos disponibles' }}
            />
          )}
        </div>
      </div>
      <Modal
        title="Escanear Preenvío"
        visible={isScanning}
        onCancel={() => setIsScanning(false)}
        footer={null}
      >
        <div id="qr-reader" style={{ width: "100%" }} />
        <Button onClick={startScanning} type="primary" block>
          Iniciar Escaneo
        </Button>
      </Modal>
      
      <Modal
        title="Confirmar Devolución"
        visible={confirmModalVisible}
        onOk={handleConfirmDevolucion}
        onCancel={handleCancelDevolucion}
        okText="Aceptar"
        cancelText="Cancelar"
      >
        <p>Se va a realizar la devolución del preenvío {preenvioToConfirm}</p>
      </Modal>
    </>
  );
};

export default Devolucion;